import { memo, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth.store'
import { sidebarApi } from '@/lib/api'
import { getDefaultSidebar } from '@/lib/navigation/defaultSidebar'
import { SidebarApiIcon } from '@/components/ui/app-icon'
import { LogOut, Navigation } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Sidebar = memo(function Sidebar() {
  const location = useLocation()
  const role = useAuthStore((s) => s.role)
  const roleName = useAuthStore((s) => s.roleName)

  const { data: serverItems } = useQuery({
    queryKey: ['sidebar', role],
    queryFn: async () => (await sidebarApi.get(role ?? undefined)).data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const items = useMemo(() => {
    if (serverItems && serverItems.length > 0) return serverItems
    return getDefaultSidebar(role)
  }, [serverItems, role])

  const isActive = (item: { path: string; match: string[] }) => {
    const rootPath = role === 'admin' ? '/admin' : '/worker'
    return item.match.some((m) => {
      if (m === rootPath) return location.pathname === m
      return location.pathname.startsWith(m)
    })
  }

  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full shrink-0">
      {/* Logo */}
      <div className="px-5 py-4 border-b border-gray-100">
        <Link to={role === 'admin' ? '/admin' : '/worker'} className="flex items-center gap-2 text-decoration-none">
          <img src="/TrueMark logo.svg" alt="TrueMark" className="h-7 w-auto" />
          <span className="font-semibold text-gray-900 text-sm">TrueMark</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
        {items.map((item) => {
          const cls = cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors no-underline',
            isActive(item)
              ? 'bg-blue-50 text-blue-700'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
          )

          return (
            <Link key={item.path} to={item.path} className={cls}>
              <SidebarApiIcon icon={item.icon} label={item.label} className="h-4 w-4 shrink-0" />
              {item.label}
            </Link>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium px-2 py-1 bg-gray-100 rounded text-gray-600">
            {roleName}
          </span>
          <div className="flex items-center gap-1">
            {role === 'admin' && (
              <Link
                to="/admin/sidebar-settings"
                className={cn(
                  'p-1.5 rounded transition-colors',
                  location.pathname === '/admin/sidebar-settings'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
                )}
                title="Настройки навигации"
              >
                <Navigation className="h-4 w-4" />
              </Link>
            )}
            <Link
              to="/login"
              className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors no-underline"
              title="Выйти"
              onClick={() => {
                useAuthStore.getState().clearAuth()
              }}
            >
              <LogOut className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </aside>
  )
})
